import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

import {
  LayoutDashboard,
  ClipboardList,
  LogOut,
  Activity,
  Timer
} from "lucide-react";

import EmployeeTasks from "./EmployeeTasks";
import TimeTracker from "./TimeTracker";

export default function EmployeeDashboard() {

  const { user, orgId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [orgName, setOrgName] = useState("");

  /* Fetch organization name */
  useEffect(() => {

    const fetchOrg = async () => {

      if (!orgId) return;

      const ref = doc(db, "organizations", orgId);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();
        setOrgName(data.basic?.companyName || "Organization");
      }

    };

    fetchOrg();

  }, [orgId]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/employee-login");
  };

  return (

    <div className="flex min-h-screen bg-[#F8FAFC]">

      {/* SIDEBAR */}

      <aside className="w-72 bg-white border-r border-slate-200 hidden md:flex flex-col sticky top-0 h-screen">

        {/* Logo */}

        <div className="flex items-center px-5 py-4">

          <img
            src="/logo.png"
            alt="Avaami Pulse"
            className="w-12 h-12 object-contain mr-2"
          />

          <h1 className="text-xl font-bold text-slate-900">
            Avaami <span className="text-[#1A73E8]">Pulse</span>
          </h1>

        </div>


        {/* Navigation */}

        <nav className="flex-1 px-5 mt-6 space-y-2">

          <SidebarLink
            active={activeTab === "overview"}
            onClick={() => setActiveTab("overview")}
            icon={<LayoutDashboard size={18} />}
            label="Dashboard"
          />

          <SidebarLink
            active={activeTab === "tasks"}
            onClick={() => setActiveTab("tasks")}
            icon={<ClipboardList size={18} />}
            label="My Tasks"
          />

          <SidebarLink
            active={activeTab === "tracker"}
            onClick={() => setActiveTab("tracker")}
            icon={<Timer size={18} />}
            label="Time Tracker"
          />

        </nav>


        {/* Logout */}

        <div className="p-6 border-t">

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-red-500 text-white py-3 rounded-xl font-semibold transition"
          >

            <LogOut size={16} />

            Sign Out

          </button>

        </div>

      </aside>



      {/* MAIN SECTION */}

      <div className="flex-1 flex flex-col">

        {/* HEADER */}

        <header className="bg-white border-b px-10 py-6 flex justify-between items-center">

          <div>

            {/* Organization */}

            <p className="text-xs text-slate-400 uppercase font-semibold">
              Organization
            </p>

            <h1 className="text-lg font-bold text-slate-900">
              {orgName || "Loading organization..."}
            </h1>


            {/* Welcome Message */}

            <p className="text-slate-600 mt-2 text-sm">
              Welcome back,{" "}
              <span className="font-semibold text-[#1A73E8]">
                {user?.displayName || "Team Member"}
              </span>
            </p>

          </div>


          {/* User Avatar */}

          <div className="h-11 w-11 rounded-xl bg-[#1A73E8] text-white flex items-center justify-center font-bold shadow">

            {user?.displayName?.charAt(0) || "U"}

          </div>

        </header>



        {/* CONTENT */}

        <main className="p-10 max-w-7xl mx-auto w-full">

          {activeTab === "overview" && (

            <div className="space-y-8">

              <div className="bg-[#1A73E8] p-10 rounded-2xl text-white shadow-lg relative overflow-hidden">

                <div className="relative z-10">

                  <h3 className="text-2xl font-semibold mb-3">
                    Personal Productivity Center
                  </h3>

                  <p className="text-blue-100 max-w-xl">

                    Manage your assigned tasks, track work sessions,
                    and maintain accurate time logs using the
                    Avaami Pulse productivity platform.

                  </p>

                  <button
                    onClick={() => setActiveTab("tasks")}
                    className="mt-6 bg-white text-[#1A73E8] px-6 py-2 rounded-lg font-semibold hover:shadow transition"
                  >
                    View My Tasks
                  </button>

                </div>

                <Activity className="absolute -bottom-10 -right-10 text-white/10 w-56 h-56" />

              </div>

            </div>

          )}

          {activeTab === "tasks" && <EmployeeTasks />}

          {activeTab === "tracker" && <TimeTracker />}

        </main>

      </div>

    </div>

  );

}


/* Sidebar Button */

const SidebarLink = ({ active, onClick, icon, label }) => (

  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition
      ${active
        ? "bg-[#1A73E8] text-white"
        : "text-slate-600 hover:bg-slate-100"
      }`}
  >

    {icon}

    {label}

  </button>

);